import React, { useEffect, useMemo, useRef, useState } from "react";
import { getArtistBookings, deleteBooking, saveBooking, getBookingById } from "../../services/bookingService";
import { getPaymentsByBooking, createPayment, deletePayment as deletePaymentApi } from "../../services/paymentService";
import PaymentReceipt from "../../components/PaymentReceipt";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useLocation } from "react-router-dom";
import { safeFormatDate } from "../../common/dateUtils";
import { updateTotalAmountForBookings } from '../../common/bookingUtils';
import api from "../../api/axiosClient";
import { bookingStatusConfig } from '../../common/bookingStatusConfig';
import { 
  calculateBookingTotal, 
  calculateBookingItemTotal,
  getLatestServicePrice as getServicePrice 
} from "../../utils/bookingCalculations";
import './iconStyles.css';

export default function ArtistBookings() {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [popupBooking, setPopupBooking] = useState<any | null>(null);
  const [paymentPopupBooking, setPaymentPopupBooking] = useState<any | null>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [receiptPayment, setReceiptPayment] = useState<any | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const [shouldPrint, setShouldPrint] = useState(false);

  // Print handler for react-to-print (cast to any to satisfy TS across versions)
  const handlePrintReceipt = useReactToPrint({
    contentRef: printRef,
    documentTitle: receiptPayment ? `Receipt_${receiptPayment.id}` : 'Receipt',
    removeAfterPrint: true,
  } as any);

  // Trigger print once content is mounted
  useEffect(() => {
    if (shouldPrint && printRef.current) {
      handlePrintReceipt?.();
      setShouldPrint(false);
    }
  }, [shouldPrint, receiptPayment, handlePrintReceipt]);
  const getTodayInputDate = () => new Date().toISOString().slice(0, 10);
  const [paymentForm, setPaymentForm] = useState<{ paidAmount: string; paidDate: string; paymentMethod: string; notes: string }>(() => ({ paidAmount: "", paidDate: getTodayInputDate(), paymentMethod: "Cash", notes: "" }));
  const [paymentWarning, setPaymentWarning] = useState<string>("");
  const [services, setServices] = useState<any[]>([]);
  const [servicePrices, setServicePrices] = useState<any[]>([]);
  const [outsourceRoles, setOutsourceRoles] = useState<any[]>([]);
  const [outsourcePersons, setOutsourcePersons] = useState<any[]>([]);
  const [bookingSlots, setBookingSlots] = useState<any[]>([]);
  const navigate = useNavigate();

  const location = useLocation();

  useEffect(() => {
    loadBookings();
  }, []);
  // Auto-open popup if a query parameter ?open={id} is provided
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('open');
    const id = raw ? Number(raw) : 0;
    if (!id || popupBooking) return;
    const found = bookings.find((b) => b.id === id);
    if (found) {
      setPopupBooking(found);
      // Clear the 'open' param but preserve any other params (e.g., status)
      const next = new URLSearchParams(location.search);
      next.delete('open');
      navigate(`/artist/bookings${next.toString() ? `?${next.toString()}` : ''}`, { replace: true });
    }
  }, [bookings, location.search, navigate, popupBooking]);


  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await getArtistBookings();
      const updatedData = updateTotalAmountForBookings(data);
      setBookings(updatedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Payments helpers
  const openPayments = async (booking: any) => {
    setPaymentPopupBooking(booking);
    try {
      const list = await getPaymentsByBooking(booking.id);
      setPayments(list);
      // default date to today when opening the add payment form
      setPaymentForm(f => ({ ...f, paidDate: getTodayInputDate() }));
    } catch (e) {
      console.error("Failed to load payments", e);
      setPayments([]);
    }
  };

  const sumPaid = (list: any[]) => list.reduce((s, p) => s + (Number(p.paidAmount) || 0), 0);

  const computeBookingTotal = (booking: any) => {
    if (booking?.bookingItems && Array.isArray(booking.bookingItems)) {
      return calculateBookingTotal(booking.bookingItems);
    }
    return Number(booking?.totalAmount) || 0;
  };

  // For payments and due calculations, prefer bookingAmount if available
  const computePayableTotal = (booking: any) => {
    const amt = Number(booking?.bookingAmount);
    return Number.isFinite(amt) && amt > 0 ? amt : computeBookingTotal(booking);
  };

  const handleAddPayment = async () => {
    if (!paymentPopupBooking) return;
    const { paidAmount, paidDate, paymentMethod, notes } = paymentForm;
    setPaymentWarning("");
    if (!paidAmount || !paidDate) {
      setPaymentWarning("Amount and date are required");
      return;
    }
    const isoDate = paidDate.endsWith("Z") ? paidDate : `${paidDate}T00:00:00Z`;
    const toAdd = Number(paidAmount);
    if (toAdd <= 0) {
      setPaymentWarning("Amount must be greater than zero");
      return;
    }
  const maxTotal = computePayableTotal(paymentPopupBooking);
    const alreadyPaid = sumPaid(payments);
    if (alreadyPaid + toAdd > maxTotal) {
      const exceed = alreadyPaid + toAdd - maxTotal;
      setPaymentWarning(`Exceeded by ₹ ${exceed.toFixed(2)}. You cannot collect more than the booking total.`);
      return;
    }
    try {
      await createPayment({
        bookingId: paymentPopupBooking.id,
        paidAmount: toAdd,
        paidDate: isoDate,
        paymentMethod,
        notes,
      });
      const list = await getPaymentsByBooking(paymentPopupBooking.id);
  setPayments(list);
  setPaymentForm({ paidAmount: "", paidDate: getTodayInputDate(), paymentMethod: "Cash", notes: "" });
      setPaymentWarning("");
      // Optionally refresh bookings to reflect totals elsewhere
    } catch (e) {
      console.error("Failed to add payment", e);
      alert("Failed to add payment");
    }
  };

  const handleDeletePayment = async (id: number) => {
    if (!paymentPopupBooking) return;
    if (!window.confirm("Delete this payment?")) return;
    try {
      await deletePaymentApi(id);
      const list = await getPaymentsByBooking(paymentPopupBooking.id);
      setPayments(list);
    } catch (e) {
      console.error("Failed to delete payment", e);
      alert("Failed to delete payment");
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      await deleteBooking(id);
      await loadBookings();
    }
  };

  // Fetch services and prices when popup opens
  useEffect(() => {
    if (popupBooking) {
      // If items not present, fetch booking details once
      if (!popupBooking.bookingItems || popupBooking.bookingItems.length === 0) {
        getBookingById(popupBooking.id)
          .then((full) => {
            // Only update if we still need items
            if (popupBooking && (!popupBooking.bookingItems || popupBooking.bookingItems.length === 0)) {
              setPopupBooking({ ...popupBooking, ...full });
            }
          })
          .catch(() => {/* ignore; keep minimal data */});
      }
      api.get('/Service').then(res => setServices(res.data));
      // Load artist-scoped service prices (fallback to query param if needed)
      api.get('/ServicePrice/artist').then(res => setServicePrices(res.data)).catch(async () => {
        const artistId = (typeof window !== 'undefined') ? Number(localStorage.getItem('makeupArtistId') || 0) : 0;
        const res2 = await api.get(`/ServicePrice?artistId=${artistId}`);
        setServicePrices(res2.data);
      });
      api.get('/OutsourceRole').then(res => setOutsourceRoles(res.data));
      api.get('/OutsourcePerson/artist').then(res => {
        const d = res.data;
        const list = Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []);
        setOutsourcePersons(list);
      });
      // Load booking slots if available (non-fatal if endpoint not present yet)
      api.get('/BookingSlot').then(res => setBookingSlots(res.data)).catch(() => setBookingSlots([]));
      // Initialize bookingAmount once if missing
      if (popupBooking.bookingAmount === undefined || popupBooking.bookingAmount === null) {
        const initial = (popupBooking.totalAmount !== undefined && popupBooking.totalAmount !== null)
          ? Number(popupBooking.totalAmount)
          : computeBookingTotal(popupBooking);
        setPopupBooking({ ...popupBooking, bookingAmount: initial });
      }
    }
  }, [popupBooking]);

  // Lock background scroll when any popup is open, and ensure modal itself scrolls
  useEffect(() => {
    const anyOpen = !!popupBooking || !!paymentPopupBooking;
    if (typeof document !== 'undefined') {
      const original = document.body.style.overflow;
      if (anyOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = original || '';
      }
      return () => {
        document.body.style.overflow = original || '';
      }
    }
  }, [popupBooking, paymentPopupBooking]);

  // Helper: unique client key for comparison (prefer id, fallback to name)
  const getClientKey = (b: any) => (b?.client?.id ?? b?.clientId ?? b?.client?.fullName ?? '').toString().toLowerCase();

  // Compute booked slot ids for a given date across OTHER bookings for this artist.
  // Same-client exception: if another booking on that date is for the same client as the popup booking,
  // its slots are not considered blocked.
  const getBookedSlotsForDate = (dateStr: string): Set<number> => {
    const result = new Set<number>();
    if (!dateStr) return result;
    const targetClientKey = popupBooking ? getClientKey(popupBooking) : '';
    for (const b of bookings) {
      if (!b || (popupBooking && b.id === popupBooking.id)) continue; // ignore current booking
      const otherClientKey = getClientKey(b);
      const items = Array.isArray(b?.bookingItems) ? b.bookingItems : [];
      for (const it of items) {
        if (it?.isDeleted) continue;
        const d = it?.bookingDate ? (it.bookingDate as string).slice(0, 10) : '';
        const sid = Number(it?.bookingSlotId);
        if (!sid || !d) continue;
        if (d === dateStr) {
          // same-client exception: allow same slot if client is same
          if (targetClientKey && otherClientKey && targetClientKey === otherClientKey) {
            continue;
          }
          result.add(sid);
        }
      }
    }
    return result;
  };

  // Helper to get latest price for a service
  const getLatestServicePrice = (serviceId: number) => {
    return getServicePrice(servicePrices, serviceId);
  };

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const statusFilterRaw = (query.get('status') || '').toLowerCase();
  const hasStatusFilter = !!statusFilterRaw;
  const filteredBookings = useMemo(() => {
    const base = hasStatusFilter ? bookings.filter(b => {
      const s = (b.status || '').toLowerCase();
      if (statusFilterRaw === 'booked') return s === 'booked' || s === 'confirmed';
      if (statusFilterRaw === 'canceled' || statusFilterRaw === 'cancelled') return s === 'canceled' || s === 'cancelled';
      if (statusFilterRaw === 'enquired' || statusFilterRaw === 'enquired') return s === 'enquired';
      return s === statusFilterRaw;
    }) : bookings;
    // Sort by eventDate ascending for grouping
    const sorted = [...base].sort((a: any, b: any) => {
      const ta = new Date(a.eventDate || 0).getTime();
      const tb = new Date(b.eventDate || 0).getTime();
      return ta - tb;
    });
    return sorted;
  }, [bookings, hasStatusFilter, statusFilterRaw]);

  return (
    <div className="p-6 bg-background min-h-screen font-sans">
      {/* Popup for booking details (grid format, editable booking items) */}
      {popupBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10 max-w-6xl w-full relative max-h-[90vh] overflow-y-auto overscroll-contain border border-secondary/20">
            <button
              className="absolute top-2 right-2 text-secondary hover:text-primary text-2xl font-bold"
              onClick={() => setPopupBooking(null)}
            >
              &times;
            </button>
            <h3 className="text-2xl font-semibold mb-6 text-secondary font-sans tracking-tight">Booking Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mb-4">
              {/* Booking Number */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-secondary">Booking Number:</label>
                <span className="border border-secondary/30 rounded-xl px-3 py-2 bg-background">{popupBooking.bookingNumber}</span>
              </div>
              {/* Client */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-secondary">Client:</label>
                <span className="border border-secondary/30 rounded-xl px-3 py-2 bg-background">{popupBooking.client?.fullName}</span>
              </div>
              {/* Event Date */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-secondary">Event Date:</label>
                <input
                  type="date"
                  value={popupBooking.eventDate?.slice(0, 10) || ''}
                  onChange={e => setPopupBooking({ ...popupBooking, eventDate: e.target.value })}
                  className="border border-secondary/30 rounded-xl px-3 py-2 w-full bg-white focus:border-primary focus:ring-2 focus:ring-primary/30 outline-none transition"
                />
              </div>
              {/* Venue */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Venue:</label>
                <input
                  type="text"
                  value={popupBooking.venue || ''}
                  onChange={e => setPopupBooking({ ...popupBooking, venue: e.target.value })}
                  className="border rounded px-2 py-1 w-full"
                />
              </div>
              {/* Total Amount */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Total Amount:</label>
                <span className="border rounded px-2 py-1 bg-gray-50">₹ {computeBookingTotal(popupBooking)}</span>
              </div>
              {/* Booking Amount */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Booking Amount:</label>
                <input
                  type="number"
                  value={popupBooking.bookingAmount ?? ''}
                  onChange={e => setPopupBooking({ ...popupBooking, bookingAmount: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="border rounded px-2 py-1 w-full"
                />
              </div>
              {/* Discount Given */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Discount Given:</label>
                <span className="border rounded px-2 py-1 bg-gray-50">₹ {Math.max(0, computeBookingTotal(popupBooking) - (Number(popupBooking.bookingAmount) || 0)).toFixed(2)}</span>
              </div>
              {/* Status */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Status:</label>
                <select
                  value={popupBooking.status}
                  onChange={e => setPopupBooking({ ...popupBooking, status: e.target.value })}
                  className="border rounded px-2 py-1 w-full"
                >
                  {Object.keys(bookingStatusConfig).map(status => (
                    <option key={status} value={status}>{bookingStatusConfig[status].label}</option>
                  ))}
                </select>
              </div>
              {/* Availability info for selected event date */}
              <div className="md:col-span-2 text-sm text-gray-600">
                {(() => {
                  const d = (popupBooking.eventDate || '').slice(0, 10);
                  if (!d) return null;
                  const blocked = getBookedSlotsForDate(d);
                  const total = bookingSlots.length;
                  const available = Math.max(0, total - blocked.size);
                  return <span>On {safeFormatDate(d)}, available slots: <strong>{available}</strong> of {total}</span>;
                })()}
              </div>
            </div>
            <div className="mb-2 flex justify-between items-center">
              <strong>Booking Items</strong>
              <button
                className="btn-primary"
                onClick={() => {
                  const newItem = {
                    id: 0,
                    isDeleted: false,
                    bookingId: popupBooking.id,
                    serviceId: '',
                    service: null,
                    outsourcePersonIds: [], // Array of outsource person IDs
                    outsourcePersons: [], // Array of outsource person objects
                    description: '',
                    serviceCount: 1,
                    amount: 0,
                    bookingDate: (popupBooking.eventDate || '').slice(0,10) || '',
                    bookingSlotId: null
                  };
                  setPopupBooking({
                    ...popupBooking,
                    bookingItems: [...(popupBooking.bookingItems || []), newItem]
                  });
                }}
              >+ Add Item</button>
            </div>
            <div className="space-y-4 mb-4">
              {popupBooking.bookingItems?.filter((it: any) => !it.isDeleted)?.map((item: any, idx: number) => (
                <div key={`item-block-${idx}-${item.id || 0}`} className="border rounded-lg p-4 bg-gray-50 relative">
                  {/* Delete icon button at top-right */}
                  <button
                    className="absolute top-2 right-2 p-1"
                    title="Delete"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this booking item?')) {
                        const all = [...popupBooking.bookingItems];
                        const originalIndex = all.findIndex((it: any) => it === item);
                        if (item.id && item.id > 0) {
                          all[originalIndex] = { ...item, isDeleted: true };
                        } else {
                          all.splice(originalIndex, 1);
                        }
                        setPopupBooking({ ...popupBooking, bookingItems: all });
                      }
                    }}
                  >
                    {/* Classic red bin/trash icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-red-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5v9.75A2.25 2.25 0 009 19.5h6a2.25 2.25 0 002.25-2.25V7.5m-10.5 0h10.5m-10.5 0V6.75A2.25 2.25 0 018.25 4.5h7.5a2.25 2.25 0 012.25 2.25V7.5m-10.5 0h10.5M9.75 10.5v4.5m4.5-4.5v4.5" />
                    </svg>
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {/* Service */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Service</label>
                      <select
                        value={(item.service?.id ?? item.serviceId) || ''}
                        onChange={e => {
                          const serviceId = Number(e.target.value);
                          const selectedService = services.find((s: any) => s.id === serviceId);
                          const price = getLatestServicePrice(serviceId);
                          const updatedItems = [...popupBooking.bookingItems];
                          updatedItems[idx] = {
                            ...item,
                            service: selectedService,
                            serviceId,
                            amount: price
                          };
                          setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                        }}
                        className="border rounded px-2 py-1 w-full"
                      >
                        <option value="">Select Service</option>
                        {services.map((s: any) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Service Count */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Number Of Makeup</label>
                      <input
                        type="number"
                        min="1"
                        value={item.serviceCount || 1}
                        onChange={e => {
                          const count = Math.max(1, Number(e.target.value) || 1);
                          const serviceId = item.service?.id || item.serviceId;
                          const price = serviceId ? getLatestServicePrice(serviceId) : (item.amount || 0);
                          const updatedItems = [...popupBooking.bookingItems];
                          updatedItems[idx] = {
                            ...item,
                            serviceCount: count,
                            amount: price, // Update price from service price list
                          };
                          setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                        }}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </div>
                    {/* Comment */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Comment</label>
                      <input
                        type="text"
                        value={item.description || ''}
                        onChange={e => {
                          const updatedItems = [...popupBooking.bookingItems];
                          updatedItems[idx] = { ...item, description: e.target.value };
                          setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                        }}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </div>
                    {/* Total Amount */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Total Amount (Auto-calculated)</label>
                      <input
                        type="number"
                        value={calculateBookingItemTotal(item.serviceCount || 1, Number(item.amount) || 0)}
                        readOnly
                        className="border rounded px-2 py-1 w-full bg-gray-100"
                      />
                    </div>
                    {/* Item Date */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Item Date</label>
                      <input
                        type="date"
                        value={(item.bookingDate || '').slice(0,10)}
                        onChange={e => {
                          const updatedItems = [...popupBooking.bookingItems];
                          updatedItems[idx] = { ...item, bookingDate: e.target.value };
                          setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                        }}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </div>
                    {/* Slot */}
                    <div className="flex flex-col gap-1">
                      <label className="font-medium">Slot</label>
                      {(() => {
                        const dateKey = (item.bookingDate || popupBooking.eventDate || '').slice(0, 10);
                        const blocked = getBookedSlotsForDate(dateKey);
                        const options = bookingSlots.filter((s: any) => !blocked.has(s.id));
                        const selectedBlocked = item.bookingSlotId && blocked.has(Number(item.bookingSlotId));
                        return (
                          <div>
                            <select
                              value={item.bookingSlotId || ''}
                              onChange={e => {
                                const val = e.target.value ? Number(e.target.value) : null;
                                const updatedItems = [...popupBooking.bookingItems];
                                updatedItems[idx] = { ...item, bookingSlotId: val };
                                setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                              }}
                              className="border rounded px-2 py-1 w-full"
                            >
                              <option value="">Select Slot</option>
                              {options.map((s: any) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                            {selectedBlocked && (
                              <div className="text-xs text-red-600 mt-1">This slot is already booked for {safeFormatDate(dateKey)} by another client.</div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Outsource Persons (Multiple Selection) */}
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label className="font-medium">Outsource Persons</label>
                      <div className="border rounded px-3 py-2 w-full bg-white min-h-[42px] flex flex-wrap gap-2">
                        {(item.outsourcePersonIds || []).map((personId: number) => {
                          const person = outsourcePersons.find((p: any) => p.id === personId);
                          return person ? (
                            <span key={personId} className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-lg text-sm">
                              {person.name}
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedItems = [...popupBooking.bookingItems];
                                  updatedItems[idx] = {
                                    ...item,
                                    outsourcePersonIds: item.outsourcePersonIds.filter((id: number) => id !== personId),
                                    outsourcePersons: (item.outsourcePersons || []).filter((p: any) => p.id !== personId)
                                  };
                                  setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                                }}
                                className="text-primary hover:text-red-600 ml-1"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                      <select
                        onChange={e => {
                          const personId = Number(e.target.value);
                          if (!personId) return;
                          const selectedPerson = outsourcePersons.find((p: any) => p.id === personId);
                          if (!selectedPerson) return;
                          const currentIds = item.outsourcePersonIds || [];
                          if (currentIds.includes(personId)) return; // Already added
                          const updatedItems = [...popupBooking.bookingItems];
                          updatedItems[idx] = {
                            ...item,
                            outsourcePersonIds: [...currentIds, personId],
                            outsourcePersons: [...(item.outsourcePersons || []), selectedPerson]
                          };
                          setPopupBooking({ ...popupBooking, bookingItems: updatedItems });
                          e.target.value = ''; // Reset dropdown
                        }}
                        value=""
                        className="border rounded px-2 py-1 w-full mt-2"
                      >
                        <option value="">+ Add Outsource Person</option>
                        {outsourcePersons
                          .filter((p: any) => !(item.outsourcePersonIds || []).includes(p.id))
                          .map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Unified save: send booking and all items in one POST. Items with id=0 are inserts; include isDeleted for deletions. */}
            <div className="flex justify-end">
              <button
                className="btn-primary px-4 py-2"
                onClick={async () => {
                  try {
                    // Slot selection validation: every booking item must have a slot selected
                    const missingSlotIdx = (popupBooking.bookingItems || []).findIndex((it: any) => !it.isDeleted && (!it.bookingSlotId || it.bookingSlotId === ''));
                    if (missingSlotIdx !== -1) {
                      alert('Please select a slot for every booking item.');
                      return;
                    }
                    // Pre-save validation: prevent saving if any item selects a blocked slot for that date (other client)
                    const conflicts: Array<{ idx: number; dateKey: string; slotId: number }> = [];
                    for (let i = 0; i < (popupBooking.bookingItems || []).length; i++) {
                      const it = popupBooking.bookingItems[i];
                      if (it?.isDeleted) continue;
                      const dateKey = (it.bookingDate || popupBooking.eventDate || '').slice(0, 10);
                      const slotId = Number(it?.bookingSlotId);
                      if (!dateKey || !slotId) continue;
                      const blocked = getBookedSlotsForDate(dateKey);
                      if (blocked.has(slotId)) {
                        conflicts.push({ idx: i, dateKey, slotId });
                      }
                    }
                    if (conflicts.length > 0) {
                      alert('One or more booking items select a slot that is already booked on that date by another client. Please choose different slots.');
                      return;
                    }
                    const calcTotal = computeBookingTotal(popupBooking);
                    const payload = {
                      id: popupBooking.id,
                      eventDate: popupBooking.eventDate,
                      venue: popupBooking.venue,
                      status: popupBooking.status,
                      bookingAmount: (popupBooking.bookingAmount === '' || popupBooking.bookingAmount === undefined || popupBooking.bookingAmount === null)
                        ? calcTotal
                        : Number(popupBooking.bookingAmount) || 0,
                      discountGiven: Math.max(0, calcTotal - (Number(popupBooking.bookingAmount) || calcTotal)),
                      bookingItems: (popupBooking.bookingItems || []).map((it: any) => ({
                        id: it.id || 0,
                        bookingId: popupBooking.id,
                        serviceId: it.service?.id || it.serviceId || 0,
                        outsourcePersonIds: it.outsourcePersonIds || [], // Send array of IDs
                        description: it.description || '',
                        serviceCount: it.serviceCount || 1,
                        amount: Number(it.amount) || 0,
                        bookingDate: it.bookingDate || null,
                        bookingSlotId: it.bookingSlotId || null,
                        isDeleted: !!it.isDeleted,
                      })),
                    };
                    await saveBooking(payload);
                    setPopupBooking(null);
                    await loadBookings();
                  } catch (err) {
                    console.error('Error saving booking:', err);
                    alert('Failed to save booking.');
                  }
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup for booking payments */}
      {paymentPopupBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-8 max-w-3xl w-full relative max-h-[90vh] overflow-y-auto overscroll-contain">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => setPaymentPopupBooking(null)}
            >
              &times;
            </button>
            <h3 className="text-xl font-bold mb-4">Payments for {paymentPopupBooking.bookingNumber}</h3>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div><strong>Total:</strong> ₹ {computePayableTotal(paymentPopupBooking)}</div>
              <div><strong>Paid:</strong> ₹ {sumPaid(payments)}</div>
              <div><strong>Due:</strong> ₹ {Math.max(0, computePayableTotal(paymentPopupBooking) - sumPaid(payments))}</div>
            </div>

            <div className="mb-4">
              <h4 className="font-semibold mb-2">Add Payment</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  type="number"
                  placeholder="Amount"
                  className="border rounded px-3 py-2"
                  value={paymentForm.paidAmount}
                  onChange={e => setPaymentForm(f => ({ ...f, paidAmount: e.target.value }))}
                />
                <input
                  type="date"
                  className="border rounded px-3 py-2"
                  value={paymentForm.paidDate}
                  onChange={e => setPaymentForm(f => ({ ...f, paidDate: e.target.value }))}
                />
                <select
                  className="border rounded px-3 py-2"
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(f => ({ ...f, paymentMethod: e.target.value }))}
                >
                  <option>Cash</option>
                  <option>Card</option>
                  <option>UPI</option>
                  <option>Bank Transfer</option>
                  <option>Other</option>
                </select>
                <input
                  type="text"
                  placeholder="Notes"
                  className="border rounded px-3 py-2"
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <div className="mt-3 flex justify-end">
                <button className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700" onClick={handleAddPayment}>Add Payment</button>
              </div>
              {paymentWarning && (
                <div className="mt-2 text-sm text-red-600">{paymentWarning}</div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 border">Paid Date</th>
                    <th className="px-3 py-2 border">Method</th>
                    <th className="px-3 py-2 border">Amount</th>
                    <th className="px-3 py-2 border">Notes</th>
                    <th className="px-3 py-2 border text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td className="px-3 py-2 border" colSpan={5}>No payments yet.</td></tr>
                  ) : (
                    payments.map(p => (
                      <tr key={p.id} className="border-t">
                        <td className="px-3 py-2 border">{safeFormatDate(p.paidDate)}</td>
                        <td className="px-3 py-2 border">{p.paymentMethod}</td>
                        <td className="px-3 py-2 border">₹ {Number(p.paidAmount).toFixed(2)}</td>
                        <td className="px-3 py-2 border">{p.notes || '-'}</td>
                        <td className="px-3 py-2 border text-right">                          
                          <button
                            className="text-primary hover:text-secondary mr-2"
                            title="Print Receipt"
                            onClick={() => {
                              setReceiptPayment({ ...p });
                              setShouldPrint(true);
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="inline w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2m-8 0h8v4H6v-4z" /></svg>
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700 "
                            title="Delete Payment"
                            onClick={() => handleDeletePayment(p.id)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="inline w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Hidden PaymentReceipt for printing */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }} aria-hidden="true">
              {receiptPayment && (
                <PaymentReceipt
                  ref={printRef}
                  payment={{
                    ...receiptPayment,
                    amount: receiptPayment.paidAmount,
                    date: receiptPayment.paidDate,
                    mode: receiptPayment.paymentMethod,
                    remarks: receiptPayment.notes,
                  }}
                  artist={{
                    name: localStorage.getItem('makeupArtistName') || 'Artist',
                    contact: localStorage.getItem('artistContact') || '',
                  }}
                  client={{
                    name: paymentPopupBooking?.client?.fullName || '',
                    contact: paymentPopupBooking?.client?.phone || paymentPopupBooking?.client?.contact || '',
                  }}
                  booking={{
                    bookingNumber: paymentPopupBooking?.bookingNumber || '',
                    eventName: paymentPopupBooking?.eventName || '',
                    venue: paymentPopupBooking?.venue || '',
                    eventDate: paymentPopupBooking?.eventDate || '',
                  }}
                  onClose={undefined}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
  <h2 className="text-2xl font-semibold text-secondary">
          My Bookings {hasStatusFilter ? (
            <span className="ml-2 text-sm text-gray-600">(Filtered: {statusFilterRaw})</span>
          ) : null}
        </h2>
        <button
          onClick={() => navigate("/artist/add-booking")}
          className="btn-primary px-4 py-2"
        >
          + New Booking
        </button>
      </div>
      {hasStatusFilter && (
        <div className="mb-4 text-sm">
          <button
            onClick={() => navigate('/artist/bookings')}
            className="text-primary hover:underline"
          >
            Clear filter
          </button>
        </div>
      )}
      {loading ? (
        <p>Loading...</p>
      ) : filteredBookings.length === 0 ? (
        <p>No bookings found.</p>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full border border-gray-200">
            <thead className="bg-highlight text-secondary">
              <tr>
                <th className="py-2 px-4 border hidden sm:table-cell">#</th>
                <th className="py-2 px-4 border">Client</th>
                <th className="py-2 px-4 border">Date</th>
                <th className="py-2 px-4 border hidden md:table-cell">Venue</th>
                <th className="py-2 px-4 border hidden md:table-cell">Amount</th>
                <th className="py-2 px-4 border">Booking Amount</th>
                <th className="py-2 px-4 border hidden lg:table-cell">Discount</th>
                <th className="py-2 px-4 border">Status</th>
                <th className="py-2 px-4 border text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {
                (() => {
                  const rows: React.ReactNode[] = [];
                  let lastDateKey = "";
                  filteredBookings.forEach((b, i) => {
                    const dateKey = (b.eventDate || '').toString().slice(0,10);
                    if (dateKey && dateKey !== lastDateKey) {
                      rows.push(
                        <tr key={`sep-${dateKey}`}>
                          <td className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold" colSpan={9}>
                            {safeFormatDate(b.eventDate)}
                          </td>
                        </tr>
                      );
                      lastDateKey = dateKey;
                    }
                    rows.push(
                      <tr key={b.id} className="hover:bg-highlight/40">
                        <td className="py-2 px-4 border hidden sm:table-cell">{i + 1}</td>
                        <td className="py-2 px-4 border">
                          <button
                            className="text-primary underline hover:text-secondary"
                            onClick={() => setPopupBooking(b)}
                          >
                            {b.client?.fullName}
                          </button>
                        </td>
                        <td className="py-2 px-4 border">{safeFormatDate(b.eventDate)}</td>
                        <td className="py-2 px-4 border hidden md:table-cell">{b.venue}</td>
                        <td className="py-2 px-4 border hidden md:table-cell">₹ {computeBookingTotal(b).toFixed(2)}</td>
                        <td className="py-2 px-4 border">₹ {(Number(b.bookingAmount ?? computeBookingTotal(b)) || 0).toFixed(2)}</td>
                        <td className="py-2 px-4 border hidden lg:table-cell">₹ {Math.max(0, computeBookingTotal(b) - (Number(b.bookingAmount ?? computeBookingTotal(b)) || 0)).toFixed(2)}</td>
                        <td className="py-2 px-4 border">
                          {(() => {
                            const config = bookingStatusConfig[b.status] || {
                              label: b.status,
                              bg: "bg-gray-100",
                              text: "text-gray-700"
                            };
                            return (
                              <span className={`px-2 py-1 rounded text-sm ${config.bg} ${config.text}`}>
                                {config.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-4 border text-center">
                          <button
                            onClick={() => openPayments(b)}
                            className="text-indigo-600 hover:text-indigo-800 mr-3"
                            title="Payments"
                          >
                            <span className="app-icon app-icon-rupee" title="Rupee">₹</span>
                          </button>
                          <button
                            onClick={() => handleDelete(b.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Delete Booking"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="app-icon app-icon-delete" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" /></svg>
                          </button>
                        </td>
                      </tr>
                    );
                  });
                  return rows;
                })()
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
